import React, { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import axios from 'axios';
import QRCode from 'qrcode';
import { useAppDispatch } from 'app/config/store';
import { getAccount } from 'app/shared/reducers/authentication';
import { getEntities as getDepositEntities } from 'app/entities/deposit-transaction/deposit-transaction.reducer';
import Swal from 'sweetalert2';
import { colors } from 'app/shared/theme/colors';
import { useTranslation } from 'app/shared/i18n';

const spinKeyframes = `
@keyframes cryptoSpin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
@keyframes cryptoGlow { 0%,100% { box-shadow: 0 0 8px rgba(99,102,241,.3);} 50% { box-shadow: 0 0 20px rgba(99,102,241,.6);} }
`;

const chip = (active: boolean) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 16px',
  borderRadius: 10,
  border: `1px solid ${active ? colors.accent : colors.border}`,
  background: active ? `${colors.accent}15` : colors.surface,
  color: active ? colors.accent : colors.muted,
  fontWeight: 700,
  fontSize: 14,
  cursor: 'pointer',
  transition: 'all .15s',
});

// Logo coin/mạng tự vẽ (không phụ thuộc ảnh ngoài) — dùng màu thương hiệu cho chuyên nghiệp.
const COIN_META: Record<string, { bg: string; sym: string }> = {
  USDT: { bg: '#26A17B', sym: '₮' },
  USDC: { bg: '#2775CA', sym: '$' },
  BNB: { bg: '#F3BA2F', sym: 'B' },
  TRX: { bg: '#EF0027', sym: 'T' },
};
const NET_COLOR: Record<string, string> = { TRON: '#EF0027', BSC: '#F3BA2F', ETH: '#627EEA' };

const CoinBadge = ({ coin, size = 22 }: { coin: string; size?: number }) => {
  const m = COIN_META[coin] || { bg: colors.accent, sym: (coin || '?').charAt(0) };
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        borderRadius: '50%',
        background: m.bg,
        color: '#fff',
        fontWeight: 800,
        fontSize: size * 0.52,
        flexShrink: 0,
        lineHeight: 1,
        boxShadow: `0 1px 4px ${m.bg}66`,
      }}
    >
      {m.sym}
    </span>
  );
};
const NetDot = ({ network }: { network: string }) => (
  <span
    style={{
      display: 'inline-block',
      width: 10,
      height: 10,
      borderRadius: '50%',
      background: NET_COLOR[network] || colors.accent,
      flexShrink: 0,
    }}
  />
);

export const CryptoCreatedDialog = (props: any) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [loadingAssets, setLoadingAssets] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [testnet, setTestnet] = useState(false);
  const [assets, setAssets] = useState<any[]>([]);

  const [network, setNetwork] = useState('');
  const [coin, setCoin] = useState('');
  const [step, setStep] = useState<'pick' | 'pay'>('pick');
  const [creating, setCreating] = useState(false);

  const [code, setCode] = useState('');
  const [address, setAddress] = useState('');
  const [expectedAmount, setExpectedAmount] = useState('');
  const [received, setReceived] = useState('0');
  const [imgData, setImgData] = useState('');

  const [status, setStatus] = useState(false);
  const [expired, setExpired] = useState(false);
  const [expireDate, setExpireDate] = useState<Date | null>(null);
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    if (props.isOpen) {
      setStep('pick');
      setNetwork('');
      setCoin('');
      setCode('');
      setAddress('');
      setExpectedAmount('');
      setReceived('0');
      setImgData('');
      setStatus(false);
      setExpired(false);
      setExpireDate(null);
      setRemaining(0);
      loadAssets();
    }
  }, [props.isOpen, props.amount]);

  const loadAssets = async () => {
    setLoadingAssets(true);
    try {
      const { data } = await axios.get('api/crypto-deposit/assets');
      setEnabled(!!data.data.enabled);
      setTestnet(!!data.data.testnet);
      setAssets(data.data.assets || []);
    } catch {
      setAssets([]);
      setEnabled(false);
    }
    setLoadingAssets(false);
  };

  const networks: string[] = Array.from(new Set(assets.map(a => a.network)));
  const coinsForNetwork: string[] = assets.filter(a => a.network === network).map(a => a.coin);

  const pickNetwork = (n: string) => {
    setNetwork(n);
    const coins = assets.filter(a => a.network === n).map(a => a.coin);
    setCoin(coins.length === 1 ? coins[0] : '');
  };

  const confirmPick = async () => {
    if (!network || !coin) return;
    setCreating(true);
    try {
      const vnd = Number(String(props.amount).replace(/\./g, ''));
      const { data } = await axios.post('api/crypto-deposit', { network, coin, amount: vnd });
      if (data.error) {
        Swal.fire({ title: t('cryptoDialog.error'), text: data.message || t('cryptoDialog.cannotCreate'), icon: 'error' });
        setCreating(false);
        return;
      }
      setCode(data.data.code);
      setAddress(data.data.address);
      setExpectedAmount(data.data.expectedAmount);
      if (data.data.createdDate && data.data.expireDate) {
        const created = new Date(data.data.createdDate + 'Z').getTime();
        const expires = new Date(data.data.expireDate + 'Z').getTime();
        setExpireDate(new Date(Date.now() + (expires - created)));
      }
      try {
        const qr = await QRCode.toDataURL(data.data.address, { width: 240, margin: 1 });
        setImgData(qr);
      } catch {
        setImgData('');
      }
      dispatch(getDepositEntities({ sort: 'createdDate,DESC' }));
      setStep('pay');
    } catch {
      Swal.fire({ title: t('cryptoDialog.error'), text: t('cryptoDialog.cannotCreate'), icon: 'error' });
    }
    setCreating(false);
  };

  // countdown
  useEffect(() => {
    if (!expireDate) return;
    const tick = () => {
      const d = Math.max(0, Math.floor((expireDate.getTime() - Date.now()) / 1000));
      setRemaining(d);
      if (d <= 0) setExpired(true);
    };
    tick();
    const tm = setInterval(tick, 1000);
    return () => clearInterval(tm);
  }, [expireDate]);

  // poll status
  useEffect(() => {
    if (status || !code || expired) return;
    const id = setInterval(checkStatus, 5000);
    return () => clearInterval(id);
  }, [status, code, expired]);

  const checkStatus = async () => {
    if (!code) return;
    try {
      const { data } = await axios.get(`api/crypto-deposit/check/${code}`);
      setReceived(data.data.receivedAmount || '0');
      if (data.data.isApproved) {
        setStatus(true);
        return;
      }
      if (data.data.expired) setExpired(true);
    } catch {
      /* ignore */
    }
  };

  // success
  useEffect(() => {
    if (status) {
      (async () => {
        await Promise.all([dispatch(getAccount()), dispatch(getDepositEntities({ sort: 'createdDate,DESC' }))]);
        if (props.handleClose) props.handleClose();
        setTimeout(() => Swal.fire({ title: t('cryptoDialog.successTitle'), text: t('cryptoDialog.successText'), icon: 'success' }), 300);
      })();
    }
  }, [status]);

  const simulate = async () => {
    try {
      await axios.post(`api/crypto-deposit/simulate/${code}`);
      await checkStatus();
    } catch {
      /* ignore */
    }
  };

  const copyText = (txt: string) => {
    if (navigator.clipboard) navigator.clipboard.writeText(txt);
  };

  const formatRemaining = (s: number) =>
    `${Math.floor(s / 60)
      .toString()
      .padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  return (
    <>
      <style>{spinKeyframes}</style>
      <Modal isOpen={props.isOpen} toggle={props.handleClose} zIndex={1060}>
        <ModalHeader toggle={props.handleClose} style={{ background: colors.surface, borderBottom: `1px solid ${colors.border}` }}>
          <span style={{ fontSize: 16, fontWeight: 700 }}>{`🪙 ${t('cryptoDialog.title')}`}</span>
          {testnet && (
            <span
              style={{
                marginLeft: 8,
                fontSize: 11,
                fontWeight: 700,
                color: '#22c55e',
                background: '#22c55e18',
                padding: '2px 8px',
                borderRadius: 6,
              }}
            >
              TESTNET
            </span>
          )}
        </ModalHeader>
        <ModalBody style={{ padding: 24 }}>
          {loadingAssets ? (
            <div style={{ textAlign: 'center', padding: 30 }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  margin: '0 auto 12px',
                  border: `3px solid ${colors.border}`,
                  borderTopColor: colors.accent,
                  borderRadius: '50%',
                  animation: 'cryptoSpin .8s linear infinite',
                }}
              />
              <div style={{ fontSize: 13, color: colors.muted }}>{t('cryptoDialog.loading')}</div>
            </div>
          ) : !enabled || assets.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 24, color: colors.muted, fontSize: 14 }}>{t('cryptoDialog.disabled')}</div>
          ) : step === 'pick' ? (
            <div>
              <div style={{ fontSize: 13, color: colors.muted, marginBottom: 8 }}>{t('cryptoDialog.pickNetwork')}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
                {networks.map(n => (
                  <div key={n} style={chip(network === n)} onClick={() => pickNetwork(n)}>
                    <NetDot network={n} />
                    {n}
                  </div>
                ))}
              </div>
              {network && (
                <>
                  <div style={{ fontSize: 13, color: colors.muted, marginBottom: 8 }}>{t('cryptoDialog.pickCoin')}</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
                    {coinsForNetwork.map(c => (
                      <div key={c} style={chip(coin === c)} onClick={() => setCoin(c)}>
                        <CoinBadge coin={c} size={20} />
                        {c}
                      </div>
                    ))}
                  </div>
                </>
              )}
              <div
                style={{
                  padding: '12px 16px',
                  borderRadius: 10,
                  background: colors.surface,
                  border: `1px solid ${colors.border}`,
                  marginBottom: 18,
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <span style={{ fontSize: 13, color: colors.muted }}>{t('cryptoDialog.amount')}</span>
                <span style={{ fontWeight: 800, color: colors.accent }}>
                  {Number(String(props.amount).replace(/\./g, '')).toLocaleString('vi-VN')} VND
                </span>
              </div>
              <button
                onClick={confirmPick}
                disabled={!coin || creating}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: 10,
                  background: !coin || creating ? colors.border : colors.accent,
                  color: '#fff',
                  border: 'none',
                  fontWeight: 700,
                  fontSize: 15,
                  cursor: !coin || creating ? 'not-allowed' : 'pointer',
                }}
              >
                {creating ? t('cryptoDialog.creating') : t('cryptoDialog.continue')}
              </button>
            </div>
          ) : (
            <div style={{ textAlign: 'center' }}>
              {expireDate && !status && (
                <div
                  style={{
                    marginBottom: 14,
                    fontWeight: 800,
                    fontSize: 18,
                    color: expired ? colors.danger : colors.accent,
                    fontFamily: "'JetBrains Mono', monospace",
                  }}
                >
                  {expired ? t('cryptoDialog.expired') : `${t('cryptoDialog.remaining')} ${formatRemaining(remaining)}`}
                </div>
              )}

              {/* Cảnh báo mạng */}
              <div
                style={{
                  marginBottom: 14,
                  padding: '8px 12px',
                  borderRadius: 10,
                  background: '#22c55e12',
                  border: '1px solid #22c55e33',
                  color: '#15803d',
                  fontSize: 12,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                <span style={{ fontSize: 14 }}>⚠️</span>
                <span>
                  {t('cryptoDialog.networkWarning', { defaultValue: 'Chỉ gửi' })} <b>{coin}</b>{' '}
                  {t('cryptoDialog.viaNetwork', { defaultValue: 'qua mạng' })} <b>{network}</b>
                </span>
              </div>

              {imgData && (
                <div style={{ ...(expired ? { opacity: 0.35, filter: 'grayscale(100%)' } : {}) }}>
                  <img src={imgData} alt="QR" style={{ maxWidth: 240, borderRadius: 12 }} />
                </div>
              )}

              {/* Số tiền cần gửi */}
              <div
                style={{
                  marginTop: 12,
                  padding: '12px 16px',
                  borderRadius: 12,
                  background: `${colors.accent}0a`,
                  border: `1px solid ${colors.accent}22`,
                }}
              >
                <div style={{ fontSize: 12, color: colors.muted, marginBottom: 6 }}>{t('cryptoDialog.sendExactly')}</div>
                <div
                  style={{
                    fontWeight: 800,
                    fontSize: 22,
                    color: colors.accent,
                    fontFamily: "'JetBrains Mono', monospace",
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  <CoinBadge coin={coin} size={26} />
                  {expectedAmount} {coin}
                </div>
              </div>

              {/* Địa chỉ */}
              <div
                onClick={() => copyText(address)}
                title={t('cryptoDialog.copy')}
                style={{
                  marginTop: 12,
                  padding: '12px 14px',
                  borderRadius: 10,
                  background: colors.surface,
                  border: `1px solid ${colors.border}`,
                  cursor: 'pointer',
                  wordBreak: 'break-all',
                  ...(expired ? { opacity: 0.4 } : {}),
                }}
              >
                <div style={{ fontSize: 11, color: colors.dim, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>👛</span>
                  {t('cryptoDialog.address')} ({network}) — {t('cryptoDialog.tapToCopy')}
                </div>
                <span style={{ fontWeight: 700, color: colors.accent, fontSize: 13, fontFamily: "'JetBrains Mono', monospace" }}>
                  {address}
                </span>
              </div>

              {/* Trạng thái */}
              {!expired && (
                <div style={{ marginTop: 16 }}>
                  {status ? (
                    <div
                      style={{
                        padding: '14px',
                        borderRadius: 12,
                        background: `${colors.success}15`,
                        border: `1px solid ${colors.success}33`,
                        color: colors.success,
                        fontWeight: 700,
                      }}
                    >
                      &#10003; {t('cryptoDialog.success')}
                    </div>
                  ) : (
                    <div
                      style={{
                        padding: '14px',
                        borderRadius: 12,
                        background: `${colors.accent}08`,
                        border: `1px solid ${colors.accent}22`,
                        animation: 'cryptoGlow 2s ease-in-out infinite',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                        <div
                          style={{
                            width: 24,
                            height: 24,
                            border: `3px solid ${colors.border}`,
                            borderTopColor: colors.accent,
                            borderRadius: '50%',
                            animation: 'cryptoSpin 1s linear infinite',
                          }}
                        />
                        <span style={{ color: colors.accent, fontWeight: 700, fontSize: 14 }}>{t('cryptoDialog.waiting')}</span>
                      </div>
                      <div style={{ marginTop: 8, fontSize: 12, color: colors.muted }}>
                        {t('cryptoDialog.received')}: {received} / {expectedAmount} {coin}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* TESTNET: nút giả lập đã nhận tiền để vận hành thử */}
              {testnet && !status && !expired && (
                <button
                  onClick={simulate}
                  style={{
                    marginTop: 14,
                    width: '100%',
                    padding: '10px',
                    borderRadius: 10,
                    background: '#22c55e',
                    color: '#fff',
                    border: 'none',
                    fontWeight: 700,
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  {t('cryptoDialog.simulate', { defaultValue: '[TESTNET] Giả lập đã nhận tiền' })}
                </button>
              )}
            </div>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};
